/**
 * DiagPostOiLedger: Diagnostics drawer -- the post-OI (quote-aware) universe ledger, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { AnalyticsMetric, badgeStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { MICRO_LABEL } from "@components/cockpit";
import { numberFmt, pctFmt, titleizeCode } from "@lib/screenerTruth";
import { type Purchase } from "@lib/screenerTypes";

export type DiagPostOiLedgerProps = {
  candidateUniverseSummary: { total: number; selected: number; ranked: number; skipped: number; };
  postOiUniverseRows: Purchase[];
  postOiUniverseSummary: { total: number; quoteOk: number; needsQuote: number; farthest: string; };
};

export default function DiagPostOiLedger({
  candidateUniverseSummary,
  postOiUniverseRows,
  postOiUniverseSummary,
}: DiagPostOiLedgerProps) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: ui.cardBorder }}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-4xl">
          <div className={MICRO_LABEL} style={mutedText}>
            Full universe after moneyness + OI
          </div>
          <div className="mt-1 font-semibold">
            Quote-aware universe ledger
          </div>
          <div className="mt-1 text-sm leading-6" style={secondaryText}>
            These are the contracts that passed right, DTE, moneyness, and open-interest filters before quote and Greek gates. Rows marked quote-needed should be visible to the CIO because IBKR REST can miss a midpoint or vega that is visible in TWS.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span style={badgeStyle("info")}>Post-OI: {postOiUniverseSummary.total}</span>
          <span style={badgeStyle("success")}>Quote OK: {postOiUniverseSummary.quoteOk}</span>
          <span style={badgeStyle(postOiUniverseSummary.needsQuote ? "warning" : "success")}>Needs quote: {postOiUniverseSummary.needsQuote}</span>
          <span style={badgeStyle("neutral")}>Farthest: {postOiUniverseSummary.farthest || "—"}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        <AnalyticsMetric
          label="Post-OI universe"
          value={numberFmt(postOiUniverseSummary.total, 0)}
          subtext="Passed DTE, moneyness, and OI filters"
        />
        <AnalyticsMetric
          label="Full-score ranked"
          value={numberFmt(candidateUniverseSummary.ranked || candidateUniverseSummary.total, 0)}
          subtext="Had usable midpoint and vega"
        />
        <AnalyticsMetric
          label="Quote / Greek incomplete"
          value={numberFmt(postOiUniverseSummary.needsQuote, 0)}
          subtext="Visible for TWS verification, not excluded silently"
        />
        <AnalyticsMetric
          label="CIO takeaway"
          value={postOiUniverseSummary.needsQuote ? "Review quote gaps" : "Universe ties out"}
          subtext={postOiUniverseSummary.needsQuote ? "Potential opportunity can be missed if this table is ignored." : "Post-OI and ranked sets are aligned."}
        />
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border" style={{ borderColor: ui.cardBorder }}>
        <table className="min-w-full text-sm">
          <thead style={{ backgroundColor: ui.rowHeader }}>
            <tr>
              {[
                "Post-OI Rank",
                "Full Rank",
                "Eligibility",
                "Quote",
                "Hydration",
                "Attempts",
                "Fields",
                "Expiry",
                "Strike",
                "DTE",
                "Moneyness",
                "OI",
                "Liquidity",
                "Sizing Max",
                "Bid",
                "Ask",
                "Mid",
                "Vega",
                "Surface",
                "Provisional",
                "Reason",
              ].map((label) => (
                <th key={label} className="px-3 py-2 text-left font-semibold" style={{ color: ui.textSecondary, whiteSpace: "nowrap" }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {postOiUniverseRows.map((p, idx) => {
              const quoteStatus = String(p.quoteStatus || "QUOTE_OK").toUpperCase();
              const quoteTone = quoteStatus === "QUOTE_OK" ? "success" : quoteStatus.includes("VEGA") && quoteStatus.includes("MID") ? "danger" : "warning";
              return (
                <tr key={`post-oi-${p.conid}-${idx}`} style={{ borderTop: `1px solid ${ui.cardBorder}`, backgroundColor: idx % 2 ? "transparent" : ui.rowAlt }}>
                  <td className="px-3 py-2 font-semibold">{numberFmt(p.provisionalUniverseRank ?? p.postOiUniverseRank ?? null, 0)}</td>
                  <td className="px-3 py-2">{p.fullScoreRank == null ? "—" : numberFmt(p.fullScoreRank, 0)}</td>
                  <td className="px-3 py-2"><span style={badgeStyle(quoteStatus === "QUOTE_OK" ? "success" : "warning")}>{titleizeCode(p.rankEligibility)}</span></td>
                  <td className="px-3 py-2"><span style={badgeStyle(quoteTone)}>{titleizeCode(quoteStatus)}</span></td>
                  <td className="px-3 py-2"><span style={badgeStyle(p.quoteHydrationComplete ? "success" : "warning")}>{titleizeCode(p.quoteHydrationStatus || quoteStatus)}</span></td>
                  <td className="px-3 py-2">{numberFmt(p.quoteHydrationAttempts ?? null, 0)}</td>
                  <td className="px-3 py-2" style={{ minWidth: 180 }}>
                    <div className="text-xs" style={mutedText}>Returned: {(p.snapshotReturnedFields || []).slice(0, 8).join(", ") || "—"}</div>
                    {(p.snapshotMissingRequestedFields || []).length ? <div className="text-xs" style={mutedText}>Missing: {(p.snapshotMissingRequestedFields || []).slice(0, 5).join(", ")}</div> : null}
                  </td>
                  <td className="px-3 py-2" style={{ whiteSpace: "nowrap" }}>{p.expiry}</td>
                  <td className="px-3 py-2">{numberFmt(p.strike, 0)}</td>
                  <td className="px-3 py-2">{numberFmt(p.dte, 0)}</td>
                  <td className="px-3 py-2">{pctFmt(p.moneyness, 2)}</td>
                  <td className="px-3 py-2">{p.openInterestStatus === "unavailable_verify" ? "Verify" : numberFmt(p.openInterest, 0)}</td>
                  <td className="px-3 py-2"><span style={badgeStyle(p.liquidityTier === "STARTER_50_99" ? "info" : p.liquidityTier === "VERIFY_OI" ? "warning" : "success")}>{titleizeCode(p.liquidityTier)}</span></td>
                  <td className="px-3 py-2">{numberFmt(p.sizingMaxInitialQty ?? p.oiCapQty ?? null, 0)}</td>
                  <td className="px-3 py-2">{numberFmt(p.bid, 2)}</td>
                  <td className="px-3 py-2">{numberFmt(p.ask, 2)}</td>
                  <td className="px-3 py-2">{numberFmt(p.mid ?? p.midPrice ?? null, 2)}</td>
                  <td className="px-3 py-2">{numberFmt(p.vega, 3)}</td>
                  <td className="px-3 py-2">{numberFmt(p.surfaceScore, 3)}</td>
                  <td className="px-3 py-2">{numberFmt(p.provisionalUniverseScore, 3)}</td>
                  <td className="px-3 py-2" style={{ minWidth: 260 }}>
                    <div className="text-xs" style={mutedText}>{p.quoteReason || p.skipReasonDetail || (quoteStatus === "QUOTE_OK" ? "Full-score eligible." : "Needs TWS quote verification.")}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
